import { api } from '../../api';
import type { SpeechForm } from '../../api/speech';
export interface StudioApp {
  id: number;
  name: string;
  entry: { screen: string; [key: string]: unknown };
  version?: number;
  [key: string]: unknown;
}
export interface StudioDocument {
  id: number;
  name?: string;
  title?: string;
  code?: string;
  document_kind?: string;
  [key: string]: unknown;
}
export interface StudioPage {
  items: StudioDocument[];
  next_cursor?: string | null;
  total?: number;
  catalog_version?: number | string;
  [key: string]: unknown;
}
export interface StudioQuery {
  q?: string;
  cursor?: string;
  format?: string;
  sort?: string;
  tab?: string;
  top?: string;
  cast?: string;
  collection?: string;
  limit?: number;
}
export interface StudioForm extends SpeechForm {
  constraints?: {
    min_photos?: number | null;
    name_max_unicode_codepoints?: number | null;
    max_photos?: number | null;
    photo?: Record<string, unknown>;
    video?: Record<string, unknown>;
    motion?: Record<string, unknown>;
    text?: Record<string, unknown>;
    transcription?: Record<string, unknown>;
    circle?: {
      policy_version: string;
      face_fields: string[];
      speech_fields: string[];
      exactly_one_face: boolean;
      exactly_one_speech: boolean;
      text_voice_field: string;
      audio_voice_field: string;
      default_format: string;
      max_duration_ms: number;
      [key: string]: unknown;
    };
  };
  fields: Array<
    SpeechForm['fields'][number] & {
      max_length?: number;
      upload:
        | (NonNullable<SpeechForm['fields'][number]['upload']> & {
            kind: 'photo' | 'audio' | 'video' | 'document';
            max_bytes?: number;
            purpose?: 'order_input' | 'reference' | 'video_input';
            /** У полей контракта экрана (screen_fields) вместо purpose — допустимые назначения загрузки (docs/59 §1) */
            purposes?: string[];
            use?: 'style' | 'scene' | 'look';
          })
        | null;
      /** Кодирование значения (docs/59): поля шаблона — строка, поля контракта экрана — JSON */
      value_encoding?: 'string' | 'json';
      /** Цена заказа при этом значении поля, если она от него зависит («Сколько фото»: {"1": 30, "2": 60, "4": 120}) */
      prices?: Record<string, number>;
      /** Значение по умолчанию, если форма его публикует */
      default_value?: string | number | boolean | null;
    }
  >;
  /** Поля контракта экрана из спецификации (docs/59-studio-backend-contracts.md §1): уходят в тот же `inputs` */
  screen_fields?: StudioForm['fields'];
  /** Ключи спецификации, которые уже покрыты полями шаблона */
  screen_contract?: { covered?: Array<{ key: string; by: string[] }> };
}
/** Все опубликованные поля формы: шаблона и контракта экрана. */
export function studioFields(form: StudioForm | null | undefined): StudioForm['fields'] {
  if (!form) return [];
  const own = form.fields ?? [];
  const keys = new Set(own.map(field => field.key));
  return [...own, ...(Array.isArray(form.screen_fields) ? form.screen_fields.filter(field => field && !keys.has(field.key)) : [])];
}
/** Цена единицы платной формы (unit_price прайса): за кадр/вариант/слайд или за секунду; per — какая нужна экрану.
    Она важнее цен документа экрана и карточки каталога: те — оформление (на проде демо-0, кнопка показала бы «0 C»). */
export function studioUnitPrice(form: StudioForm | null | undefined, per?: 'variant' | 'second'): number | null {
  if (!form?.chargeable) return null;
  const value = form.unit_price;
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) return null;
  return !per || form.unit_price_per === per ? value : null;
}
/** Цена приложения за единицу на экране. Платная форма: единица прайса, иначе положительная цена карточки, иначе сумма формы
    (ноль карточки у платной формы — не цена). Без формы или бесплатно — цена карточки. */
export function studioAppUnit(cardAmount: number | null, form: StudioForm | null | undefined, per?: 'variant' | 'second'): number | null {
  if (!form?.chargeable) return cardAmount;
  const unit = studioUnitPrice(form, per);
  if (unit !== null) return unit;
  if (cardAmount !== null && cardAmount > 0) return cardAmount;
  return form.unit_price_per ? null : form.expected_price;
}
/** Цена на кнопке и в заказе: цена формы, если поле с ценами по значению (`prices`) её не уточняет. Клиент цен не пересчитывает. */
export function studioExpectedPrice(form: StudioForm, inputs: Record<string, string>): number {
  for (const field of studioFields(form)) {
    const value = inputs[field.key] ?? (field.default_value == null ? undefined : String(field.default_value));
    const price = value !== undefined && field.prices ? field.prices[value] : undefined;
    if (typeof price === 'number' && Number.isSafeInteger(price) && price >= 0) return price;
  }
  return form.expected_price;
}
export const studioApi = {
  allTools: async (signal?: AbortSignal): Promise<StudioPage> => {
    const items: StudioDocument[] = [],
      cursors = new Set<string>();
    let cursor: string | undefined;
    do {
      const page = await studioApi.list('tools', { limit: 50, cursor }, signal);
      items.push(...page.items);
      cursor = page.next_cursor ?? undefined;
      if (cursor && cursors.has(cursor)) throw new Error('Повторился курсор каталога инструментов.');
      if (cursor) cursors.add(cursor);
    } while (cursor);
    return { items: [...new Map(items.map(item => [item.id, item])).values()] };
  },
  apps: (signal?: AbortSignal) => api.request<{ items: StudioApp[] }>('/app/studio/apps', { query: { limit: 50 }, signal }),
  home: (signal?: AbortSignal) => api.request<Record<string, unknown>>('/app/studio/home', { signal }),
  catalog: (signal?: AbortSignal) => api.request<Record<string, unknown>>('/app/studio/catalog', { signal }),
  app: (id: number, signal?: AbortSignal) => api.request<StudioApp>(`/app/studio/apps/${id}`, { signal }),
  /** Состав нижней строки (tabs_list) зависит от вкладки верхней строки, поэтому top входит в запрос (§6.5) */
  collection: (id: number | string, top?: string, signal?: AbortSignal) =>
    api.request<StudioDocument>(`/app/studio/app-collections/${id}`, {
      query: top && top !== 'all' ? { top } : undefined,
      signal,
    }),
  list: async (kind: 'apps' | 'app-collections' | 'search' | 'tools', query: StudioQuery = {}, signal?: AbortSignal) => {
    /* Набор порядков у каждого эндпоинта свой, и на чужой он отвечает 409 studio_sort_unavailable
       (проверено на dev-стенде: search и tools принимают только new и name, apps — ещё pop и cheap,
       подборки не принимают ни pop, ни cheap). Защита от этого одна — не навязывать умолчание:
       сюда порядок попадает только выбранным человеком в меню ⇅ (BrowseState.sortPicked), а меню
       собрано из available_sorts ответа, поэтому такой порядок эндпоинт заведомо принимает.

       Запоминать набор из прошлого ответа нельзя: один и тот же `apps` обслуживает и каталог, и
       страницу подборки, а наборы у них разные — запомненный от каталога срезал «Короче»
       на странице подборки, и лента молча показывалась в другом порядке. */
    const page = await api.request<StudioPage>(`/app/studio/${kind}`, { query: { limit: 20, ...query }, signal });
    if (kind !== 'search') return page;
    // Поиск отдаёт три массива — tools, collections, apps (docs/03-api.md §6.8), а не общий items.
    // Приводим к одной ленте: вид записи дальше читает documentKind.
    const group = (value: unknown, documentKind: string) =>
      Array.isArray(value) ? value.filter(item => !!item && typeof item === 'object').map(item => ({ ...(item as object), document_kind: documentKind })) : [];
    const merged = [
      ...group((page as Record<string, unknown>).tools, 'tool'),
      ...group((page as Record<string, unknown>).collections, 'app_collection'),
      ...group((page as Record<string, unknown>).apps, 'app'),
    ] as StudioDocument[];
    return merged.length || !Array.isArray(page.items) ? { ...page, items: merged } : page;
  },
  form: (id: number, signal?: AbortSignal) => sharedForm(id, signal),
};
/* Одна форма — один запрос, пока он в пути: экран создания открывался тремя запросами /form одного шаблона (форма заказа, её
   перезапуск эффекта сразу после монтирования и форма стиля движка «Фото»), и кнопка «Создать» ждала самый поздний ответ — на
   медленной сети на секунды дольше. Запрос общий; каждый получает свою копию ответа; отменили все, кто ждал, — отменяется и запрос
   (новая ревизия формы после отмены прежней читает форму заново, а не берёт старый ответ) */
const copy = <T,>(value: T): T => {
  try {
    return structuredClone(value);
  } catch {
    return value;
  }
};
const FORMS = new Map<number, { answer: Promise<StudioForm>; stop: AbortController; waiting: number }>();
function sharedForm(id: number, signal?: AbortSignal): Promise<StudioForm> {
  if (signal?.aborted) return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
  let shared = FORMS.get(id);
  if (!shared) {
    const stop = new AbortController();
    const entry = { answer: api.request<StudioForm>(`/app/studio/apps/${id}/form`, { signal: stop.signal }), stop, waiting: 0 };
    const done = () => {
      if (FORMS.get(id) === entry) FORMS.delete(id);
    };
    entry.answer.then(done, done);
    FORMS.set(id, entry);
    shared = entry;
  }
  const own = shared;
  own.waiting++;
  return new Promise<StudioForm>((resolve, reject) => {
    const abort = () => {
      own.waiting--;
      if (own.waiting <= 0) {
        if (FORMS.get(id) === own) FORMS.delete(id);
        own.stop.abort();
      }
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', abort, { once: true });
    own.answer.then(
      value => {
        signal?.removeEventListener('abort', abort);
        resolve(normalizeStudioForm(copy(value)));
      },
      failure => {
        signal?.removeEventListener('abort', abort);
        reject(failure);
      },
    );
  });
}
/** Поля контракта экрана (`screen_fields`) приходят с `values: null` у загрузок и объектов,
    а экраны читают `values` как массив — приводим один раз здесь, а не в каждом экране. */
export function normalizeStudioForm(form: StudioForm): StudioForm {
  const field = (item: StudioForm['fields'][number]) => ({ ...item, values: Array.isArray(item.values) ? item.values : [] });
  return {
    ...form,
    fields: Array.isArray(form.fields) ? form.fields.filter(Boolean).map(field) : [],
    ...(Array.isArray(form.screen_fields) ? { screen_fields: form.screen_fields.filter(Boolean).map(field) } : {}),
  };
}
/** Скидка «Сделать дешевле» (03-api §9.3): форма публикует `cheap: {available, price, percent?}` — цену со скидкой считает сервер.
    Поля нет или скидка недоступна — null («Для этой работы скидки нет»). */
export function studioCheapOffer(form: StudioForm | null | undefined): { price: number; percent?: number } | null {
  const raw = (form as unknown as Record<string, unknown> | null)?.cheap;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const cheap = raw as Record<string, unknown>;
  const price = cheap.price;
  if (cheap.available !== true || typeof price !== 'number' || !Number.isSafeInteger(price) || price < 0) return null;
  const percent = typeof cheap.percent === 'number' && Number.isFinite(cheap.percent) ? cheap.percent : undefined;
  return { price, ...(percent !== undefined ? { percent } : {}) };
}

/** Скидка «дешевле» — только для той цены, для которой форма её посчитала (expected_price формы, D11):
    при другом составе (больше фото, длиннее запись) цена скидки к заказу не относится */
export function studioCheapOfferAt(form: StudioForm | null | undefined, price: number | null | undefined): { price: number; percent?: number } | null {
  const offer = studioCheapOffer(form);
  return offer && form && price === form.expected_price ? offer : null;
}
